import { MsalProvider, useMsal } from '@azure/msal-react';
import { useEffect, useLayoutEffect, useState } from 'react';

import { BrowserRouter as Router } from 'react-router-dom';
import { PageLayout } from './components/PageLayout';
import AppRoutes from './components/AppRoutes';
import { loginRequest } from './authConfig';
import { saveSurveyData } from './utils/surveyData';
import { getClient, setClient, setIsFirstCanvassCreated, setIsTutorialComp } from './utils/globalState';
import { fetchClientById, createClient } from './utils/clients';
import { fetchGraphMe, getAccessToken } from './utils/auth';
import { logger } from './utils/logger';

// Import styles
import './styles/App.css';
import './styles/Dashboard.css';

window.sendLog = saveSurveyData;

// Authenticated content component (uses MSAL hooks)
const AuthenticatedContent = () => {
    const { instance } = useMsal();
    const activeAccount = instance.getActiveAccount();
    const [clientReady, setClientReady] = useState(false);
    

    // Seed minimal client before routes render so children can use getClient() immediately
    useLayoutEffect(() => {
        try {
            const oid = instance.getActiveAccount()?.idTokenClaims?.oid || activeAccount?.localAccountId;
            const role = instance.getActiveAccount()?.idTokenClaims?.roles?.[0] || '';
            if (activeAccount && oid) {
                const existing = getClient();
                if (!existing || existing.clientId !== oid) {
                    setClient({ clientId: oid, userViewRole: existing?.userViewRole || {}, early: true, role });
                }
            }
        } catch {}
    }, [activeAccount, instance]);

    const handleRedirect = () => {
        instance
            .loginRedirect({
                ...loginRequest,
                prompt: 'create',
            })
            .catch((error) => logger.log(error));
    };
    useEffect(() => {
        let cancelled = false;
        const syncClient = async () => {
            try {
                if (!activeAccount) { setClientReady(true); return; }
                const oid = instance.getActiveAccount()?.idTokenClaims?.oid || activeAccount?.localAccountId;
                if (!oid) { setClientReady(true); return; }
                const email = activeAccount?.username || '';
                const orgFromEmail = email.includes('@') ? email.split('@')[1] : undefined;

                const accessToken = await getAccessToken({ instance, account: activeAccount, scopes: ['User.Read'] });
                const baseSelect = 'displayName,jobTitle,companyName,department,streetAddress,city,state,postalCode,country,userPrincipalName,id,mail,customSecurityAttributes,appRoles,assignedRoles';
                const data = await fetchGraphMe({ accessToken, select: baseSelect });

                let client = getClient();
                if (!client || client.early) {
                    client = await fetchClientById({ clientId: oid }).catch(() => null);
                }
                if (client) {
                    client.userViewRole = data.customSecurityAttributes?.userViewRole || {};
                    client.role = activeAccount?.idTokenClaims?.roles?.[0] || '';
                    setClient(client);
                } else {
                    const role = instance.getActiveAccount()?.idTokenClaims?.roles?.[0] || '';
                    const created = await createClient({
                        clientId: oid,
                        body: { clientId: oid, isUser: true, ...(orgFromEmail ? { organization: orgFromEmail } : {}, role) }
                    });
                    created.userViewRole = data?.customSecurityAttributes?.userViewRole || {};
                    setClient(created);
                    setIsTutorialComp(Boolean(created?.isTutorialComp));
                    setIsFirstCanvassCreated(Boolean(created?.isFirstCanvassCreated));
                }
            } catch {
            } finally {
                if (!cancelled) setClientReady(true);
            }
        };
        syncClient();
        return () => { cancelled = true; };
    }, [activeAccount, instance]);
    
    return (
        <div className="App">
            {/* <Container fluid> */}
                {clientReady ? <AppRoutes /> : null}
            {/* </Container> */}
        </div>
    );
};

// Non-authenticated content component (no MSAL hooks)
const NonAuthenticatedContent = () => {
    return (
        <div className="App">
            {/* <Container fluid> */}
                <AppRoutes />
            {/* </Container> */}
        </div>
    );
};

// Initialize dynamic questions
var isAuthProject = true;

// Main App component
const App = ({ instance }) => {
    return (
        <>
            {isAuthProject ? (
                <MsalProvider instance={instance}>
                    <Router>
                        <PageLayout isAuthProject={false}>
                            <AuthenticatedContent />
                        </PageLayout>
                    </Router>
                </MsalProvider>
            ) : (
                <Router>
                    <PageLayout isAuthProject={false}>
                        <NonAuthenticatedContent />
                    </PageLayout>
                </Router>
            )}
        </>
    );
};

export default App;